import {
	ApiMeta,
	Label,
	LaravelResponse,
	PagedSingleSelectField,
	PaginatedOptionsFetchConfig,
	SelectDefaultOption,
	SelectSingleChangeHandler,
	useFormatDateTime,
} from "@dgs/core";
import { metaToPagination } from "@dgs/core/src/utils/laravelPagination";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { eventService } from "../../shared/api/event/eventService";
import { AnalyticsFeedbackEventOptionResource } from "../analyticsTypes";

const Small = styled.small<{ $isSelected: boolean }>`
	font-size: ${({ theme }) => theme.fonts.sizes.small};
	color: ${({ theme, $isSelected }) => ($isSelected ? theme.colors.dropdown.active : theme.colors.palette.grey[70])};
`;

const StyledLabel = styled(Label)`
	width: fit-content;
	margin-top: ${({ theme }) => theme.spacing(1)};
`;

interface Props {
	name: string;
	label: string;
	onChange?: SelectSingleChangeHandler<AnalyticsFeedbackEventOptionResource | null>;
	required?: boolean;
	disabled?: boolean;
	fetchConfig?: PaginatedOptionsFetchConfig<AnalyticsFeedbackEventOptionResource, any>;
}

export const defaultAnalyticsEventFetchConfig = {
	dataMapper: (res: LaravelResponse<AnalyticsFeedbackEventOptionResource[]>) => res.data,
	queryKey: eventService.optionsForFeedbackAnalyticsKey,
	queryFn: (search: string, page: number) =>
		eventService.optionsForFeedbackAnalytics(search, page).then(({ data }) => data),
	paginationMapper: (result: LaravelResponse<AnalyticsFeedbackEventOptionResource[]>) =>
		metaToPagination(result.meta as ApiMeta),
};

export const AnalyticsEventSelect: FC<Props> = ({
	name,
	label,
	onChange,
	required,
	disabled,
	fetchConfig = defaultAnalyticsEventFetchConfig,
}) => {
	const { t } = useTranslation();
	const formatDateTime = useFormatDateTime();

	return (
		<PagedSingleSelectField
			name={name}
			label={label}
			fetchConfig={fetchConfig}
			clearable={true}
			required={required}
			disabled={disabled}
			getLabel={(o) => o.name}
			onChange={onChange}
			renderOption={(o, isSelected, isHovered) => (
				<SelectDefaultOption $isSelected={isSelected} $isHovered={isHovered}>
					<div>{o.name}</div>
					<Small $isSelected={isSelected}>
						{`${formatDateTime(o.start, { dateStyle: "short" })} - ${formatDateTime(o.end, { dateStyle: "short" })}`}
					</Small>
					<div>
						<Small $isSelected={isSelected}>{o.eventTypeName}</Small>
					</div>
					{o.disable?.disabled && <StyledLabel size="small" color="danger">{`${t(o.disable.info)}`}</StyledLabel>}
				</SelectDefaultOption>
			)}
		/>
	);
};
